import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/db';
import { useAuth } from '../lib/auth';
import { useSettings } from '../lib/settings/settingsContext';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Activity, 
  Download,
  Calendar,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

interface MetricsSummary {
  metric_name: string;
  metric_value: number;
  metric_description: string;
}

interface TimeSeriesData {
  period_start: string;
  period_end: string;
  [key: string]: string | number;
}

interface ChartData {
  signups: TimeSeriesData[];
  spending: TimeSeriesData[];
  activity: TimeSeriesData[];
  fees: TimeSeriesData[];
}

type TimeInterval = 'day' | 'week' | 'month';
type DateRange = '7d' | '30d' | '90d' | 'custom';

export function AdminMetrics() {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<MetricsSummary[]>([]);
  const [chartData, setChartData] = useState<ChartData>({
    signups: [],
    spending: [],
    activity: [],
    fees: []
  });
  
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [interval, setInterval] = useState<TimeInterval>('day');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Check if user is admin
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  const getDateRange = (): { startDate: Date; endDate: Date } => {
    const endDate = new Date();
    let startDate = new Date();

    switch (dateRange) {
      case '7d':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate.setTime(new Date(customEndDate).getTime());
        }
        break;
    }

    return { startDate, endDate };
  };

  const fetchMetrics = async () => {
    try {
      setRefreshing(true);
      const { startDate, endDate } = getDateRange();
      
      // Fetch summary metrics
      const { data: summaryData, error: summaryError } = await supabase
        .rpc('get_platform_metrics_summary', {
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString()
        });

      if (summaryError) throw summaryError;
      setSummary(summaryData || []);

      // Fetch time series data
      const [signupsResult, spendingResult, activityResult, feesResult] = await Promise.all([
        supabase.rpc('get_user_signup_metrics', {
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString(),
          p_interval_type: interval
        }),
        supabase.rpc('get_spending_metrics', {
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString(),
          p_interval_type: interval
        }),
        supabase.rpc('get_user_activity_metrics', {
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString(),
          p_interval_type: interval
        }),
        supabase.rpc('get_fee_earnings_metrics', {
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString(),
          p_interval_type: interval
        })
      ]);

      if (signupsResult.error) throw signupsResult.error;
      if (spendingResult.error) throw spendingResult.error;
      if (activityResult.error) throw activityResult.error;
      if (feesResult.error) throw feesResult.error;

      setChartData({
        signups: signupsResult.data || [],
        spending: spendingResult.data || [],
        activity: activityResult.data || [],
        fees: feesResult.data || []
      });

      setError(null);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError('Failed to load metrics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const exportToCSV = () => {
    const { startDate, endDate } = getDateRange();
    
    // Create CSV content
    let csvContent = `Wayja Platform Metrics Export\n`;
    csvContent += `Date Range: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    // Summary metrics
    csvContent += `SUMMARY METRICS\n`;
    csvContent += `Metric,Value,Description\n`;
    summary.forEach(metric => {
      csvContent += `"${metric.metric_name}","${metric.metric_value}","${metric.metric_description}"\n`;
    });
    
    // Time series data
    csvContent += `\nUSER SIGNUPS\n`;
    csvContent += `Date,New Users,Cumulative Users\n`;
    chartData.signups.forEach(row => {
      csvContent += `"${new Date(row.period_start).toLocaleDateString()}","${row.new_users}","${row.cumulative_users}"\n`;
    });
    
    csvContent += `\nSPENDING METRICS\n`;
    csvContent += `Date,Total Spend,Bet Count,Unique Users,Average Bet Size\n`;
    chartData.spending.forEach(row => {
      csvContent += `"${new Date(row.period_start).toLocaleDateString()}","${row.total_spend}","${row.bet_count}","${row.unique_users}","${row.average_bet_size}"\n`;
    });
    
    csvContent += `\nFEE EARNINGS\n`;
    csvContent += `Date,Platform Fees,Affiliate Fees,Creator Fees,Total Fees\n`;
    chartData.fees.forEach(row => {
      csvContent += `"${new Date(row.period_start).toLocaleDateString()}","${row.platform_fees}","${row.affiliate_fees}","${row.creator_fees}","${row.total_fees}"\n`;
    });

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `wayja-metrics-${startDate.toISOString().split('T')[0]}-${endDate.toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMetrics();
    }
  }, [dateRange, interval, customStartDate, customEndDate, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="py-8 px-4 md:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-800">Access denied. Admin privileges required.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-8 px-4 md:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">Platform Metrics</h1>
        </div>
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
          </div>
          <p className="text-center text-gray-500 mt-4">Loading metrics data...</p>
        </div>
      </div>
    );
  }

  const getSummaryValue = (metricName: string): number => {
    const metric = summary.find(m => m.metric_name === metricName);
    return metric ? metric.metric_value : 0;
  };

  return (
    <div className="py-8 px-4 md:px-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Platform Metrics</h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={fetchMetrics}
            disabled={refreshing}
            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Interval</label>
            <select
              value={interval}
              onChange={(e) => setInterval(e.target.value as TimeInterval)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{getSummaryValue('total_users').toLocaleString()}</p>
              <p className="text-xs text-gray-500">+{getSummaryValue('new_users_period')} this period</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Volume</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(getSummaryValue('total_volume'))}</p>
              <p className="text-xs text-gray-500">{getSummaryValue('total_bets')} trades</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Bet</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(getSummaryValue('average_bet_size'))}</p>
              <p className="text-xs text-gray-500">{getSummaryValue('active_users')} active users</p>
            </div>
            <BarChart3 className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Fees</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(getSummaryValue('total_fees_earned'))}</p>
              <p className="text-xs text-gray-500">Platform revenue</p>
            </div>
            <DollarSign className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Signups Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">User Signups</h3>
          <div className="space-y-2">
            {chartData.signups.slice(-10).map((item, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">
                  {new Date(item.period_start).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-blue-600">
                    +{item.new_users} new
                  </span>
                  <span className="text-sm text-gray-500">
                    {item.cumulative_users} total
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Spending Metrics Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Daily Spending</h3>
          <div className="space-y-2">
            {chartData.spending.slice(-10).map((item, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">
                  {new Date(item.period_start).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-green-600">
                    {formatCurrency(Number(item.total_spend))}
                  </span>
                  <span className="text-sm text-gray-500">
                    {item.bet_count} bets
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* User Activity Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">User Activity</h3>
          <div className="space-y-2">
            {chartData.activity.slice(-10).map((item, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">
                  {new Date(item.period_start).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-purple-600">
                    {item.active_users_betting} betting
                  </span>
                  <span className="text-sm text-gray-500">
                    {item.active_users_login} active
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fee Earnings Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Fee Earnings</h3>
          <div className="space-y-2">
            {chartData.fees.slice(-10).map((item, index) => (
              <div key={index} className="py-2 border-b border-gray-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">
                    {new Date(item.period_start).toLocaleDateString()}
                  </span>
                  <span className="text-sm font-medium text-yellow-600">
                    {formatCurrency(Number(item.total_fees))}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Platform: {formatCurrency(Number(item.platform_fees))}</span>
                  <span>Affiliate: {formatCurrency(Number(item.affiliate_fees))}</span>
                  <span>Creator: {formatCurrency(Number(item.creator_fees))}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Metrics Table */}
      <div className="mt-8 bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Detailed Metrics</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {summary.map((metric, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {metric.metric_name.replace(/_/g, ' ').toUpperCase()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {metric.metric_name.includes('rate') || metric.metric_name.includes('percentage') 
                      ? `${metric.metric_value.toFixed(2)}%`
                      : metric.metric_name.includes('fee') || metric.metric_name.includes('volume') || metric.metric_name.includes('bet_size')
                      ? formatCurrency(metric.metric_value)
                      : metric.metric_value.toLocaleString()
                    }
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {metric.metric_description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
